'use client';
import React, { useEffect, useState } from 'react';
import CheckoutError from './checkout-error-message';
import ExtendError from './extend-error-message';
import Button from '~/components/button';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import styles from './progressive-form.module.scss';
import {
  limitExceededErrorHeading,
  limitExceededErrorMessage,
  PROGRESSIVE_TRANSACTION_TYPE,
  PROGRESSIVE_TENDER_TYPE,
  statesErrorHeading,
  statesErrorMessage,
  PROGRESSIVE_ERROR_EVENT_LABELS,
} from '~/modules/payment/progressive/constants';
import {
  progressiveGetPaymentEstimate,
  submitProgressiveLeaseLookup,
} from '~/modules/payment/progressive/utils/progressive-submit-actions';
import {
  IGetPricing,
  ILeaseLookup,
  IPaymentEstimate,
} from '~/interfaces/commerce-types';
import {
  encodedSSN,
  getProgressiveTotal,
  getProgressiveNonLeasableItems,
} from '../utils';
import ProgressiveLeaseModal from './progressive-lease-modal';
import {
  ProgressiveConfiguration,
  BlockedState,
} from '~/gql/__generated__/types';
import { WMAP_PREFIX } from '../../constants';
import {
  sendProgressiveApplyUtag,
  sendProgressiveLookupUtag,
} from '~/utils/tealium';
import { getHostName } from '~/utils/utils';
import { Modal, Theme } from '@mfrm/design-library';

interface FormData {
  mobileNumber: string;
  last4SSN: string;
}

interface PrevFormState {
  prevMobileNum: string;
  prevSSN: string;
}

interface IProgressiveFormProps {
  checkoutCart: ISalesOrderResponse;
  progressiveOrderReview: (pricingInfo: IGetPricing) => void;
  progressiveConfigs: ProgressiveConfiguration;
  cartId: string;
  learnHowSideDrawer: React.ReactNode;
}

// Initialize the attempts count
let attempts = 0;

const ProgressiveForm: React.FC<IProgressiveFormProps> = ({
  checkoutCart,
  progressiveOrderReview,
  progressiveConfigs,
  cartId,
  learnHowSideDrawer,
}) => {
  const [formData, setFormData] = useState<FormData>({
    mobileNumber: '',
    last4SSN: '',
  });
  const [PrevFormState, setPrevFormState] = useState<PrevFormState>({
    prevMobileNum: '',
    prevSSN: '',
  });

  const [phoneNumErr, setPhoneNumErr] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [ssnErr, setSsnErr] = useState<string>('');
  const [leaseLookupInProgress, setLeaseLookupInProgress] = useState(false);
  const [invalidAttempts, setInvalidAttempts] = useState<boolean>(false);
  const [noLease, setNoLease] = useState<boolean>(false);
  const [leaseApprovedModal, setLeaseApprovedModal] = useState<boolean>(false);
  const [paymentEstimate, setPaymentEstimate] = useState<IPaymentEstimate>();
  const [leaseDetails, setLeaseDetails] = useState<ILeaseLookup>();
  const [isCartTotalLimitExceeded, setIsCartTotalLimitExceeded] =
    useState<boolean>(false);
  const [isAddressMismatch, setIsAddressMismatch] = useState<boolean>(false);
  const [addressToMatch, setAddressToMatch] = useState<string>('');
  const progressiveContent = progressiveConfigs?.progressive;
  const { nonLeasableItemError, blockedStatesForExtend, nonLeasableItems } =
    progressiveConfigs?.progressive || {};
  const [isInputValid, setIsInputValid] = useState(false);

  const progressiveTotalAmount = getProgressiveTotal(
    checkoutCart,
    (progressiveContent.progressiveStateDeliveryFee || []).filter(
      (state): state is string => !!state,
    ),
    false,
  );

  const minimumTotal =
    progressiveContent.progressiveMinimumTotalValidation?.minimumTotal ?? 300;
  const cartTotalLimitError = progressiveTotalAmount < minimumTotal;

  const shippingAddressState = checkoutCart?.ISOStateCode;
  const isProhibited =
    checkoutCart &&
    progressiveContent.prohibitedStatesValidation?.prohibitedStatesForPL?.find(
      (state) => state === shippingAddressState,
    );

  const nonLeaseableItems = getProgressiveNonLeasableItems(
    checkoutCart,
    (blockedStatesForExtend || []).filter(
      (state): state is BlockedState => state !== null,
    ),
    nonLeasableItems,
  );
  const isBlockedStateForExtend = nonLeaseableItems?.length > 0 || false;
  const isFormValid = () =>
    isInputValid &&
    !isProhibited &&
    !cartTotalLimitError &&
    !isBlockedStateForExtend &&
    (!isAddressMismatch ||
      PrevFormState.prevMobileNum !== formData.mobileNumber ||
      PrevFormState.prevSSN !== formData.last4SSN);

  const handleLookup = async (
    mobileFromIframe?: string,
    ssnFromIframe?: string,
  ) => {
    try {
      setLeaseLookupInProgress(true);
      const mobile = mobileFromIframe ?? formData.mobileNumber;
      const ssn = ssnFromIframe ?? formData.last4SSN;
      if (!mobile || !ssn) return;

      resetAttemptsAndSetPrevFormState();

      if (attempts >= 3) return;

      sendProgressiveLookupUtag();
      const data = await submitLeaseLookup(mobile, ssn);

      if (data) {
        await processLeaseData(data);
      } else {
        console.error('Error: Unable to remove the item from the cart');
      }
    } catch (error) {
      console.error('Error in lease lookup:', error);
    } finally {
      setLeaseLookupInProgress(false);
    }
  };

  const resetAttemptsAndSetPrevFormState = () => {
    if (
      attempts >= 3 &&
      (formData.mobileNumber !== PrevFormState.prevMobileNum ||
        formData.last4SSN !== PrevFormState.prevSSN)
    ) {
      attempts = 0;
    }

    setPrevFormState({
      prevMobileNum: formData.mobileNumber,
      prevSSN: formData.last4SSN,
    });
  };

  const submitLeaseLookup = async (mobile: string, ssn: string) => {
    const getSsnEncoded = encodedSSN(ssn);
    const numericPhoneNumber = mobile.replace(/\D/g, '');
    return await submitProgressiveLeaseLookup(
      `${numericPhoneNumber}`,
      getSsnEncoded,
      progressiveConfigs?.progressive?.progressiveTenderType ||
        PROGRESSIVE_TENDER_TYPE,
      progressiveConfigs?.progressive?.progressiveTransactionType ||
        PROGRESSIVE_TRANSACTION_TYPE,
    );
  };

  const processLeaseData = async (data: ILeaseLookup | null) => {
    if (attempts >= 2) {
      setInvalidAttempts(true);
    }
    attempts++;

    if (!data?.Status) {
      setNoLease(true);
      setIsCartTotalLimitExceeded(false);
      setIsAddressMismatch(false);
      setFormData({
        ...formData,
        last4SSN: '',
      });
      return;
    }

    if (data?.ApprovalAmount < progressiveTotalAmount) {
      setIsCartTotalLimitExceeded(true);
      setNoLease(false);
      attempts = 0;
      return;
    }

    if (data.HomeAddress?.Zip !== checkoutCart?.ZipCode) {
      setNoLease(false);
      setIsAddressMismatch(true);
      setIsCartTotalLimitExceeded(false);
      if (data?.HomeAddress) {
        const addressToMatch = `${data.HomeAddress?.StreetAddress1} ${data.HomeAddress?.City}, ${data.HomeAddress?.State} ${data.HomeAddress?.Zip} `;
        setAddressToMatch(addressToMatch);
      }
      setFormData({
        ...formData,
        last4SSN: '',
      });
      return;
    } else {
      setLeaseDetails(data);
      setNoLease(false);
      setIsAddressMismatch(false);

      const estimate = await progressiveGetPaymentEstimate(
        progressiveTotalAmount,
        progressiveConfigs?.progressive?.progressiveTenderType ||
          PROGRESSIVE_TENDER_TYPE,
        progressiveConfigs?.progressive?.progressiveTransactionType ||
          PROGRESSIVE_TRANSACTION_TYPE,
        checkoutCart.ISOStateCode || '',
        checkoutCart?.QuotationId || checkoutCart?.SalesId || cartId || '',
      );

      if (estimate) {
        setPaymentEstimate(estimate);
        setLeaseApprovedModal(true);
        setInvalidAttempts(false);
        attempts = 0;
      }
    }
  };

  const handleApplyNowClick = () => {
    sendProgressiveApplyUtag();
    if (progressiveContent.progressiveApplyNowForm?.progressiveApplyNowLink) {
      setIsOpen(true);
    }
  };

  const applyNowCtaId = `${WMAP_PREFIX}-progressive-apply-now-btn`;
  const leaseLookupCtaId = `${WMAP_PREFIX}-progressive-lease-lookup-btn`;

  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const progressiveUrl = `${process.env.NEXT_PUBLIC_PROGRESSIVE_CONNECT_URL}${process.env.NEXT_PUBLIC_PROGRESSIVE_CONNECT_MERCHANT_KEY}`;
  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      const eventHost = getHostName(event.origin);
      const requestHost = getHostName(window.location.origin);
      // Only allow messages from the same domain as the parent
      const isTrustedOrigin = eventHost === requestHost;
      if (!isTrustedOrigin) {
        console.warn('Blocked message from untrusted origin:', event.origin);
        return;
      }

      const { type, payload, valid } = event.data || {};
      switch (type) {
        case 'PROGRESSIVE_INPUT_STATE':
          setIsInputValid(valid);
          setPhoneNumErr(event.data.phoneError);
          setSsnErr(event.data.ssnError);
          return;

        case 'PROGRESSIVE_SECURE_DATA':
          const { mobile, ssn } = payload || {};
          setFormData({ mobileNumber: mobile, last4SSN: ssn });
          handleLookup(mobile, ssn);
          return;

        default:
          return;
      }
    };

    window.addEventListener('message', eventHandler);
    return () => window.removeEventListener('message', eventHandler);
  }, []);

  return (
    <div className={styles.container}>
      <h3 className={styles.headingSteps} tabIndex={0}>
        Step 1:
      </h3>
      <p className={styles.infoText} tabIndex={0}>
        {progressiveContent.progressiveContext?.step1FirstParagraph}{' '}
        {progressiveContent.progressiveContext?.step1SecondParagraph}
      </p>
      <div className={styles.infoCtaSection}>
        <div className={styles.applyNowSection}>
          <Button
            id={applyNowCtaId}
            className={styles.applyNowCta}
            variant="secondary"
            onClick={handleApplyNowClick}
          >
            {
              progressiveContent.progressiveApplyNowForm
                ?.progressiveApplyNowCTAName
            }
          </Button>
        </div>
        <div className={styles.learnHowSection}>{learnHowSideDrawer}</div>
      </div>
      {cartTotalLimitError && !isProhibited && (
        <CheckoutError
          {...{
            errorHeading:
              progressiveContent.progressiveMinimumTotalValidation
                ?.errorHeading || '',
            errorMessage:
              progressiveContent.progressiveMinimumTotalValidation
                ?.errorMessage || '',
            eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.cartTotalBelowMinimum,
          }}
        />
      )}

      {isBlockedStateForExtend && !isProhibited && (
        <ExtendError
          {...{
            errorHeading: nonLeasableItemError?.title,
            errorMessage: nonLeasableItemError?.message,
            errorList: nonLeaseableItems,
            eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.nonLeasableItem,
          }}
        />
      )}

      {isProhibited && (
        <CheckoutError
          {...{
            errorHeading:
              progressiveContent.prohibitedStatesValidation
                ?.statesErrorHeading ?? statesErrorHeading,
            errorMessage:
              progressiveContent.prohibitedStatesValidation
                ?.statesErrorMessage ?? statesErrorMessage,
            eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.blockedState,
          }}
        />
      )}
      <h3 className={`${styles.headingSteps} ${styles.second}`} tabIndex={0}>
        Step 2:
      </h3>
      <p className={styles.infoText} tabIndex={0}>
        {progressiveContent.progressiveContext?.step2FirstParagraph}
      </p>

      <div
        className={`
    ${styles.secureIframeContainer}
    ${phoneNumErr || ssnErr ? styles.errorActive : ''}
    ${phoneNumErr && ssnErr ? styles.doubleError : ''}
  `}
      >
        <iframe
          id="progressiveFrame"
          title="Progressive Iframe"
          ref={iframeRef}
          src="/makeapayment/progressive-secure-frame"
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: '135px', border: '0' }}
          scrolling="no"
        />
      </div>

      {isCartTotalLimitExceeded && (
        <div>
          <CheckoutError
            {...{
              errorHeading:
                progressiveContent.progressiveCartTotalExceededErrorMsg
                  ?.limitExceededErrorHeading || limitExceededErrorHeading,
              errorMessage:
                progressiveContent.progressiveCartTotalExceededErrorMsg
                  ?.limitExceededErrorMessage || limitExceededErrorMessage,
              errorTestId: '',
              eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.cartTotalExceedsLimit,
            }}
          />
        </div>
      )}
      {isAddressMismatch && (
        <div>
          <CheckoutError
            {...{
              errorHeading:
                progressiveContent.progressiveValidation
                  ?.progressiveAddressMismatch?.errorHeading,
              errorMessage:
                progressiveContent.progressiveValidation?.progressiveAddressMismatch?.errorMessage.replace(
                  '<pl-address>',
                  addressToMatch,
                ) ?? '',
              eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.addressMismatch,
            }}
          />
        </div>
      )}

      {noLease && (
        <CheckoutError
          {...{
            errorHeading: invalidAttempts
              ? progressiveContent.progressiveValidation
                  ?.leaseNotFoundThreeTries?.errorHeading
              : progressiveContent.progressiveValidation?.leaseNotFound
                  ?.errorHeading,
            errorMessage: invalidAttempts
              ? progressiveContent.progressiveValidation
                  ?.leaseNotFoundThreeTries?.errorMessage
              : progressiveContent.progressiveValidation?.leaseNotFound
                  ?.errorMessage,
            invalidAttempts,
            progressiveContactNum: progressiveContent.progressiveContactNum,
            eventLabel: PROGRESSIVE_ERROR_EVENT_LABELS.leaseInfoNotFound,
          }}
        />
      )}
      {!isCartTotalLimitExceeded && (
        <Button
          id={leaseLookupCtaId}
          className={styles.submitButton}
          onClick={() => {
            iframeRef.current?.contentWindow?.postMessage(
              { type: 'REQUEST_SUBMIT' },
              '*',
            );
          }}
          aria-disabled={!isFormValid()}
          loading={leaseLookupInProgress}
          disabled={!isFormValid()}
        >
          {!leaseLookupInProgress && (
            <span tabIndex={0} aria-label="lease lookup button">
              Lookup Progressive Lease
              {!isFormValid() && <span className="sr-only">disabled</span>}
            </span>
          )}
        </Button>
      )}
      {leaseDetails && (
        <ProgressiveLeaseModal
          isModalOpen={leaseApprovedModal}
          toggleModal={() => setLeaseApprovedModal(false)}
          paymentEstimate={paymentEstimate}
          leaseDetails={leaseDetails}
          total={progressiveTotalAmount}
          checkoutCart={checkoutCart}
          progressiveOrderReview={(pricingInfo) =>
            progressiveOrderReview(pricingInfo)
          }
          progressiveConfigs={progressiveConfigs}
          cartId={cartId}
        />
      )}
      {
        <Theme>
          <Modal
            open={isOpen}
            onClose={() => setIsOpen(false)}
            overlay
            portal
            title="Apply with Progressive Leasing"
            width={530}
            height={600}
          >
            <iframe
              src={progressiveUrl}
              title="Progressive Leasing"
              className={styles.progressiveLeasingIframe}
            />
          </Modal>
        </Theme>
      }
    </div>
  );
};

export default ProgressiveForm;
